import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { IconButton } from './IconButton'
import { AlertTriangle, CheckCircle2, Info, X, ICON_SIZE, ICON_STROKE } from '@/components/icons'

/** Transient confirmations. */

export type ToastTone = 'success' | 'error' | 'info'

interface Toast {
  id: number
  tone: ToastTone
  message: string
}

interface ToastContextValue {
  toast: (message: string, tone?: ToastTone) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function useToast(): ToastContextValue {
  const value = useContext(ToastContext)
  if (!value) throw new Error('useToast must be used inside <ToastProvider>')
  return value
}

const AUTO_DISMISS_MS = 4000

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id))
  }, [])

  const toast = useCallback(
    (message: string, tone: ToastTone = 'success') => {
      // Date.now() is fine as an id here — it is a render key, not a timestamp
      const id = Date.now() + Math.random()
      setToasts((current) => [...current, { id, tone, message }])

      // Errors stay until dismissed. An error that disappears on its own is.
      if (tone !== 'error') {
        setTimeout(() => { dismiss(id) }, AUTO_DISMISS_MS)
      }
    },
    [dismiss],
  )

  const value = useMemo(() => ({ toast }), [toast])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        // polite, not assertive: a success confirmation should not interrupt
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed right-4 bottom-4 z-50 flex flex-col gap-2"
      >
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={() => { dismiss(t.id) }} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

const TONES = {
  success: { icon: CheckCircle2, cls: 'border-success-border bg-success-bg text-success' },
  error: { icon: AlertTriangle, cls: 'border-danger-border bg-danger-bg text-danger' },
  info: { icon: Info, cls: 'border-line bg-surface text-text' },
} as const

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const { icon: Icon, cls } = TONES[toast.tone]
  return (
    <div
      className={cn(
        'pointer-events-auto flex max-w-sm items-start gap-2 rounded-md border px-3 py-2.5',
        'shadow-raised',
        cls,
      )}
    >
      <Icon size={ICON_SIZE.sm} strokeWidth={ICON_STROKE} aria-hidden className="mt-0.5 shrink-0" />
      <p className="flex-1 text-sm">{toast.message}</p>
      <IconButton icon={X} label="Dismiss" size="sm" onClick={onDismiss} className="-mt-0.5 -mr-1" />
    </div>
  )
}
